import { makeAutoObservable } from "mobx";
import { IdentityAudit, IdentityAuditRequest } from "../../models/IdentityAudit";
import { identityAuditApi } from "../../apis/IdentityAudit";

export class IdentityAuditPageModel {
    candidates: IdentityAudit[] = [];
    processed: IdentityAudit[] = [];
    selectedAudit: IdentityAudit | null = null;
    showRefuseDialog: boolean = false;
    showConfirmDialog: boolean = false;
    refuseReason: string = "";
    loading: boolean = false;
    pageSize: number = 10;
    candidatesPage: number = 1;
    processedPage: number = 1;

    constructor() {
        makeAutoObservable(this);
    }

    get pagedCandidates(): IdentityAudit[] {
        const start = (this.candidatesPage - 1) * this.pageSize;
        return this.candidates.slice(start, start + this.pageSize);
    }

    get pagedProcessed(): IdentityAudit[] {
        const start = (this.processedPage - 1) * this.pageSize;
        return this.processed.slice(start, start + this.pageSize);
    }

    setCandidatesPage(page: number) {
        this.candidatesPage = page;
    }

    setProcessedPage(page: number) {
        this.processedPage = page;
    }

    async loadCandidates() {
        this.loading = true;
        try {
            this.candidates = await identityAuditApi.getAllCandidates();
            const maxPage = Math.max(1, Math.ceil(this.candidates.length / this.pageSize) || 1);
            if (this.candidatesPage > maxPage) {
                this.candidatesPage = maxPage;
            }
        } finally {
            this.loading = false;
        }
    }

    async loadProcessed() {
        this.loading = true;
        try {
            this.processed = await identityAuditApi.getAllProcessed();
            const maxPage = Math.max(1, Math.ceil(this.processed.length / this.pageSize) || 1);
            if (this.processedPage > maxPage) {
                this.processedPage = maxPage;
            }
        } finally {
            this.loading = false;
        }
    }

    setSelectedAudit(audit: IdentityAudit | null) {
        this.selectedAudit = audit;
    }

    setShowRefuseDialog(show: boolean) {
        this.showRefuseDialog = show;
        if (!show) {
            this.refuseReason = "";
        }
    }

    setShowConfirmDialog(show: boolean) {
        this.showConfirmDialog = show;
    }

    setRefuseReason(reason: string) {
        this.refuseReason = reason;
    }

    async approveAudit() {
        if (!this.selectedAudit) return;
        
        const request: IdentityAuditRequest = {
            id: this.selectedAudit.id,
            approved: true
        };

        this.loading = true;
        try {
            await identityAuditApi.identityAudit(request);
            await this.loadCandidates();
            await this.loadProcessed();
            this.setSelectedAudit(null);
            this.setShowConfirmDialog(false);
        } finally {
            this.loading = false;
        }
    }

    async refuseAudit() {
        if (!this.selectedAudit || !this.refuseReason) return;

        const request: IdentityAuditRequest = {
            id: this.selectedAudit.id,
            approved: false,
            refuseReason: this.refuseReason
        };

        this.loading = true;
        try {
            await identityAuditApi.identityAudit(request);
            await this.loadCandidates();
            await this.loadProcessed();
            this.setSelectedAudit(null);
            this.setShowRefuseDialog(false);
        } finally {
            this.loading = false;
        }
    }
}
